// Quick Risk Creation Modal
// Simplified risk creation for dashboard

import React, { useState, useEffect } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import { Autocomplete, AutocompleteOption } from '@/components/ui/Autocomplete';
import { RiskService } from '@/services/riskService';
import { transformationsService } from '@/services/transformations';
import { useLanguage } from '@/state/language/LanguageProvider';
import { useAuth } from '@/state/auth/AuthProvider';
import { toast } from 'react-hot-toast';
import api from '@/services/api';

interface QuickRiskCreationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRiskCreated?: (risk: any) => void;
}

export const QuickRiskCreationModal: React.FC<QuickRiskCreationModalProps> = ({
  isOpen,
  onClose,
  onRiskCreated
}) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadingEntities, setLoadingEntities] = useState(false);
  const [entityOptions, setEntityOptions] = useState<AutocompleteOption[]>([]);
  const [entitySearchValue, setEntitySearchValue] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    severity: 'MEDIUM',
    probability: 'MEDIUM',
    escalation_level: '',
    entity_id: ''
  });

  // Use Risk Service for proper risk creation
  const riskService = new RiskService();

  // Map escalation level to API endpoint and entity type
  const getEntityEndpoint = (level: string): { endpoint: string; entityType: string; useAlignmentAPI?: boolean; useHierarchyAPI?: boolean; requiresEntity?: boolean } | null => {
    const mapping: Record<string, { endpoint: string; entityType: string; useAlignmentAPI?: boolean; useHierarchyAPI?: boolean; requiresEntity?: boolean }> = {
      'VISION': { endpoint: `/alignment/visions/${user?.tenantId}`, entityType: 'vision', useAlignmentAPI: true, requiresEntity: true },
      'STRATEGY': { endpoint: `/alignment/strategies/${user?.tenantId}`, entityType: 'strategy', useAlignmentAPI: true, requiresEntity: true },
      'PROGRAM': { endpoint: '/generic/transformations?hierarchy_type=PROGRAM', entityType: 'program', requiresEntity: true },
      'PROJECT': { endpoint: '/generic/transformations?hierarchy_type=PROJECT', entityType: 'project', requiresEntity: true },
      'ORGANIZATION': { endpoint: '/hierarchy/business', entityType: 'organization', useHierarchyAPI: true, requiresEntity: true },
      'FUNCTION': { endpoint: '/hierarchy/functional', entityType: 'function', useHierarchyAPI: true, requiresEntity: true },
      'GEOGRAPHY': { endpoint: '/hierarchy/geographic', entityType: 'geography', useHierarchyAPI: true, requiresEntity: true },
      'PROCESS': { endpoint: '/generic/processes', entityType: 'process', requiresEntity: true },
      'GLOBAL': { endpoint: '', entityType: 'global', requiresEntity: false }
    };
    return mapping[level] || null;
  };

  // Load entities based on selected escalation level
  useEffect(() => {
    const loadEntities = async () => {
      if (!formData.escalation_level) {
        setEntityOptions([]);
        setFormData(prev => ({ ...prev, entity_id: '' }));
        return;
      }

      const entityConfig = getEntityEndpoint(formData.escalation_level);
      if (!entityConfig) {
        setEntityOptions([]);
        return;
      }

      // If this level doesn't require entity selection (e.g., GLOBAL), skip loading
      if (!entityConfig.requiresEntity) {
        setEntityOptions([]);
        setFormData(prev => ({ ...prev, entity_id: '' }));
        return;
      }

      setLoadingEntities(true);
      try {
        let response;
        if (entityConfig.endpoint.includes('transformations')) {
          // Use transformations service
          const transformations = await transformationsService.getTransformations();
          const filtered = transformations.filter(t => 
            formData.escalation_level === 'PROGRAM' ? t.hierarchy_type === 'PROGRAM' : t.hierarchy_type === 'PROJECT'
          );
          setEntityOptions(filtered.map(t => ({ id: t.id, label: t.name })));
        } else if (entityConfig.useAlignmentAPI) {
          // Use alignment API (for visions and strategies)
          response = await api.get(entityConfig.endpoint);
          // Alignment API returns { success: true, data: [...] }
          const entities = response.data.success ? response.data.data : [];
          setEntityOptions(entities.map((e: any) => ({ 
            id: e.id, 
            label: e.name || e.title || e.display_name || 'Unnamed' 
          })));
        } else if (entityConfig.useHierarchyAPI) {
          // Use hierarchy API (for business, functional, geographic hierarchies)
          response = await api.get(entityConfig.endpoint);
          // Hierarchy API returns { success: true, data: [...] }
          const entities = response.data.success ? response.data.data : (response.data.data || response.data || []);
          setEntityOptions(entities.map((e: any) => ({ 
            id: e.id, 
            label: e.name || e.display_name || e.title || 'Unnamed' 
          })));
        } else {
          // Use generic API
          response = await api.get(entityConfig.endpoint);
          const entities = response.data.data || response.data || [];
          setEntityOptions(entities.map((e: any) => ({ 
            id: e.id, 
            label: e.name || e.display_name || e.title || 'Unnamed' 
          })));
        }
      } catch (error: any) {
        console.error(`Error loading ${entityConfig.entityType} entities:`, error);
        console.error('Error details:', error.response?.data || error.message);
        console.error('Endpoint attempted:', entityConfig.endpoint);
        setEntityOptions([]);
      } finally {
        setLoadingEntities(false);
      }
    };

    loadEntities();
  }, [formData.escalation_level]);



  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.description.trim()) {
      toast.error(t('risk.quickCreate.descriptionRequired'));
      return;
    }

    setLoading(true);
    try {
      // Clean up form data
      const cleanedFormData = {
        ...formData,
        escalation_level: formData.escalation_level === '' ? null : formData.escalation_level
      };

      const riskData: any = {
        name: cleanedFormData.name, // Use 'name' as per database schema
        description: cleanedFormData.description,
        severity: cleanedFormData.severity || 'MEDIUM',
        probability: cleanedFormData.probability || 'MEDIUM',
        escalation_level: cleanedFormData.escalation_level || null,
        identified_by_id: user?.id, // Person who identified the risk (creator)
        current_owner_id: user?.id, // Owner defaults to creator
        tenant_id: user?.tenantId
      };

      // Add entity association based on escalation level
      if (formData.entity_id && formData.escalation_level) {
        const entityConfig = getEntityEndpoint(formData.escalation_level);
        if (entityConfig) {
          const entityFieldMap: Record<string, string> = {
            'VISION': 'vision_id',
            'STRATEGY': 'strategy_id',
            'PROGRAM': 'transformation_id',
            'PROJECT': 'transformation_id',
            'ORGANIZATION': 'business_hierarchy_id',
            'FUNCTION': 'functional_hierarchy_id',
            'GEOGRAPHY': 'geographic_hierarchy_id',
            'PROCESS': 'process_id',
            'GLOBAL': 'asset_id'
          };
          const fieldName = entityFieldMap[formData.escalation_level];
          if (fieldName) {
            riskData[fieldName] = formData.entity_id;
          }
        }
      }

      const createdRisk = await riskService.createRisk(riskData);
      
      toast.success(t('risk.quickCreate.success'));
      onRiskCreated?.(createdRisk);
      handleClose();
    } catch (error) {
      console.error('Error creating risk:', error);
      toast.error(t('risk.quickCreate.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
      setFormData({
        name: '',
        description: '',
        severity: 'MEDIUM',
        probability: 'MEDIUM',
        escalation_level: '',
        entity_id: ''
      });
      setEntitySearchValue('');
      onClose();
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Reset entity selection when escalation level changes
    if (field === 'escalation_level') {
      setFormData(prev => ({
        ...prev,
        entity_id: ''
      }));
      setEntitySearchValue('');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={t('risk.quickCreate.title')}>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">
              {t('risk.quickCreate.description')} *
            </label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder={t('risk.quickCreate.descriptionPlaceholder')}
              rows={3}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="severity" className="block text-sm font-medium text-gray-700 mb-1">
                {t('risk.quickCreate.severity')}
              </label>
              <Select
                value={formData.severity}
                onChange={(e) => handleInputChange('severity', e.target.value)}
                className="w-full"
                options={[
                  { value: 'LOW', label: t('risk.severity.low') || 'Low' },
                  { value: 'MEDIUM', label: t('risk.severity.medium') || 'Medium' },
                  { value: 'HIGH', label: t('risk.severity.high') || 'High' },
                  { value: 'CRITICAL', label: t('risk.severity.critical') || 'Critical' }
                ]}
              />
            </div>

            <div>
              <label htmlFor="probability" className="block text-sm font-medium text-gray-700 mb-1">
                {t('risk.quickCreate.probability')}
              </label>
              <Select
                value={formData.probability}
                onChange={(e) => handleInputChange('probability', e.target.value)}
                className="w-full"
                options={[
                  { value: 'LOW', label: t('risk.probability.low') || 'Low' },
                  { value: 'MEDIUM', label: t('risk.probability.medium') || 'Medium' },
                  { value: 'HIGH', label: t('risk.probability.high') || 'High' }
                ]}
              />
            </div>
          </div>

          <div>
            <label htmlFor="escalation_level" className="block text-sm font-medium text-gray-700 mb-1">
              {t('risk.quickCreate.riskLevel')}
            </label>
            <Select
              value={formData.escalation_level}
              onChange={(e) => handleInputChange('escalation_level', e.target.value)}
              className="w-full"
              options={[
                { value: '', label: t('risk.quickCreate.optional') },
                { value: 'VISION', label: t('risk.levels.vision') },
                { value: 'STRATEGY', label: t('risk.levels.strategy') },
                { value: 'PROGRAM', label: t('risk.levels.program') },
                { value: 'PROJECT', label: t('risk.levels.project') },
                { value: 'ORGANIZATION', label: t('risk.levels.organization') },
                { value: 'FUNCTION', label: t('risk.levels.function') },
                { value: 'GEOGRAPHY', label: t('risk.levels.geography') },
                { value: 'PROCESS', label: t('risk.levels.process') },
                { value: 'GLOBAL', label: t('risk.levels.global') || 'Global' }
              ]}
            />
          </div>

          {/* Entity selection - shown when escalation level is selected and requires entity */}
          {formData.escalation_level && getEntityEndpoint(formData.escalation_level)?.requiresEntity && (
            <div>
              <label htmlFor="entity_id" className="block text-sm font-medium text-gray-700 mb-1">
                {t('risk.quickCreate.selectEntity') || `Select ${formData.escalation_level}`}
              </label>
              {loadingEntities ? (
                <div className="text-sm text-[var(--color-secondary-text)] py-2">Loading...</div>
              ) : entityOptions.length > 0 ? (
                <Autocomplete
                  options={entityOptions}
                  value={formData.entity_id}
                  onChange={(value) => handleInputChange('entity_id', value)}
                  onSelect={(option) => {
                    handleInputChange('entity_id', option?.id || '');
                    setEntitySearchValue(option?.label || '');
                  }}
                  searchValue={entitySearchValue}
                  onSearchChange={setEntitySearchValue}
                  placeholder={t('risk.quickCreate.searchEntity') || 'Type to search or select...'}
                  loading={loadingEntities}
                />
              ) : (
                <div className="text-sm text-gray-500 py-2">
                  {t('risk.quickCreate.noEntities') || 'No entities available'}
                </div>
              )}
            </div>
          )}


          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {loading ? t('common.creating') : t('risk.quickCreate.createRisk')}
            </Button>
          </div>
        </form>
    </Modal>
  );
};
